import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import { ToastContext } from "context/ToastContext";
import {
  useHstImportMutation,
  IHSTImportResult,
  IHSTImportRowResult,
} from "store/queries/integration";
import { sizer } from "layout/styles/styled/sizer";

type ExpandedSection =
  | "created"
  | "updated"
  | "skipped"
  | "duplicates"
  | "errors"
  | null;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(4)};
  min-width: 500px;
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(2)};
`;

const FileLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
`;

const FileInfo = styled.span`
  font-size: 12px;
  color: var(--text-color-secondary);
`;

const SelectedFileDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${sizer(2)};
  background-color: var(--surface-ground);
  border-radius: 4px;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(3)};
`;

const ResultsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${sizer(2)};
`;

type SummaryCardVariant = "success" | "error" | "warning" | "info" | "neutral";

const SummaryCard = styled.div<{
  variant?: SummaryCardVariant;
  clickable?: boolean;
  expanded?: boolean;
}>`
  padding: ${sizer(2)};
  border-radius: 4px;
  text-align: center;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: all 0.15s ease;
  border: 2px solid transparent;
  background-color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "var(--green-50)";
      case "error":
        return "var(--red-50)";
      case "warning":
        return "var(--yellow-50)";
      default:
        return "var(--surface-ground)";
    }
  }};

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      filter: brightness(0.95);
    }
  `}

  ${({ expanded, variant }) => {
    if (!expanded) return "";
    const borderColors: Record<string, string> = {
      success: "var(--green-400)",
      error: "var(--red-400)",
      warning: "var(--yellow-400)",
    };
    return `border-color: ${
      (variant && borderColors[variant]) || "var(--primary-400)"
    };`;
  }}

  .count {
    font-size: 24px;
    font-weight: bold;
    color: ${({ variant }) => {
      switch (variant) {
        case "success":
          return "var(--green-600)";
        case "error":
          return "var(--red-600)";
        case "warning":
          return "var(--yellow-600)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .label {
    font-size: 12px;
    color: var(--text-color-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .expand-icon {
    font-size: 10px;
  }
`;

const DetailItem = styled.div`
  padding: ${sizer(2)};
  border-bottom: 1px solid var(--surface-border);
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${sizer(2)};

  &:last-child {
    border-bottom: none;
  }

  .patient-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .patient-name {
    font-weight: 600;
  }

  .dos {
    color: var(--text-color-secondary);
    font-size: 12px;
  }

  .badge {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
  }

  .badge-hst {
    background-color: var(--blue-100);
    color: var(--blue-700);
  }

  .badge-manual {
    background-color: var(--purple-100);
    color: var(--purple-700);
  }

  .error-message {
    color: var(--red-600);
    font-size: 12px;
    max-width: 300px;
    word-break: break-word;
  }
`;

const DetailList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
`;

const DetailSection = styled.div`
  margin-top: ${sizer(2)};
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${sizer(1)};

  .section-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-color-secondary);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${sizer(8)};
  gap: ${sizer(2)};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizer(2)};
  padding: ${sizer(3)};
  background-color: var(--blue-50);
  border: 1px solid var(--blue-200);
  border-radius: 4px;
  font-size: 13px;
  color: var(--blue-700);

  .pi-info-circle {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

interface IHSTImportDialogProps {
  visible: boolean;
  onHide: () => void;
  accountId: string;
  onSuccess?: () => void;
}

export function HSTImportDialog({
  visible,
  onHide,
  accountId,
  onSuccess,
}: IHSTImportDialogProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [scheduledAppointmentsFile, setScheduledAppointmentsFile] =
    useState<File | null>(null);
  const [insuranceInfoFile, setInsuranceInfoFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<IHSTImportResult | null>(
    null
  );
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);

  const [hstImport, { isLoading }] = useHstImportMutation();

  // Filter results into categories
  const categorizedResults = useMemo(() => {
    if (!importResult?.results) {
      return {
        created: [],
        updated: [],
        skipped: [],
        duplicates: [],
        errors: [],
      };
    }

    const created: IHSTImportRowResult[] = [];
    const updated: IHSTImportRowResult[] = [];
    const skipped: IHSTImportRowResult[] = [];
    const duplicates: Array<
      IHSTImportRowResult & { duplicateType: "hst" | "manual" }
    > = [];
    const errors: IHSTImportRowResult[] = [];

    importResult.results.forEach((row) => {
      if (row.success && row.updated) {
        updated.push(row);
      } else if (row.success && !row.updated) {
        created.push(row);
      } else if (
        row.skipped &&
        (row.errors?.PAYER_EXCLUDED || row.errors?.SECONDARY_PAYER_EXCLUDED)
      ) {
        skipped.push(row);
      } else if (row.skipped && row.errors?.ALREADY_CURRENT) {
        duplicates.push({ ...row, duplicateType: "hst" });
      } else if (row.skipped && row.errors?.DUPLICATE) {
        duplicates.push({ ...row, duplicateType: "manual" });
      } else if (!row.success && row.errors) {
        errors.push(row);
      }
    });

    return { created, updated, skipped, duplicates, errors };
  }, [importResult?.results]);

  const handleScheduledAppointmentsSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setScheduledAppointmentsFile(e.files[0]);
    }
  };

  const handleInsuranceInfoSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setInsuranceInfoFile(e.files[0]);
    }
  };

  const handleImport = async () => {
    if (!scheduledAppointmentsFile || !insuranceInfoFile) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select both CSV files before importing",
      });
      return;
    }

    try {
      const result = await hstImport({
        accountId,
        scheduledAppointments: scheduledAppointmentsFile,
        insuranceInfo: insuranceInfoFile,
      }).unwrap();

      setImportResult(result.data);

      if (result.data.successCount > 0 || result.data.updateCount > 0) {
        const parts = [];
        if (result.data.successCount > 0) {
          parts.push(`created ${result.data.successCount}`);
        }
        if (result.data.updateCount > 0) {
          parts.push(`updated ${result.data.updateCount}`);
        }
        toastElement?.show({
          severity: "success",
          summary: "Import Complete",
          detail: `Successfully ${parts.join(" and ")} orders`,
        });
        onSuccess?.();
      }

      if (result.data.errorCount > 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Import Completed with Errors",
          detail: `${result.data.errorCount} rows had errors`,
        });
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Import Failed",
        detail: error.data?.message || "An error occurred during import",
      });
    }
  };

  const handleClose = () => {
    setScheduledAppointmentsFile(null);
    setInsuranceInfoFile(null);
    setImportResult(null);
    setExpandedSection(null);
    onHide();
  };

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const canImport =
    scheduledAppointmentsFile && insuranceInfoFile && !isLoading;

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={importResult ? "Close" : "Cancel"}
        onClick={handleClose}
        className="p-button-text"
      />
      {!importResult && (
        <Button
          label="Import"
          onClick={handleImport}
          disabled={!canImport}
          loading={isLoading}
        />
      )}
    </div>
  );

  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span>Importing orders... This may take a few moments.</span>
        </LoadingContainer>
      );
    }

    if (importResult) {
      const hasCreated = categorizedResults.created.length > 0;
      const hasUpdated = categorizedResults.updated.length > 0;
      const hasSkipped = categorizedResults.skipped.length > 0;
      const hasDuplicates = categorizedResults.duplicates.length > 0;
      const hasErrors = categorizedResults.errors.length > 0;

      return (
        <ResultsContainer>
          <ResultsSummary>
            <SummaryCard variant="info">
              <div className="count">{importResult.totalRows}</div>
              <div className="label">Records</div>
            </SummaryCard>
            <SummaryCard
              variant="success"
              clickable={hasCreated}
              expanded={expandedSection === "created"}
              onClick={() => hasCreated && toggleSection("created")}
            >
              <div className="count">{importResult.successCount}</div>
              <div className="label">
                Created
                {hasCreated && (
                  <i
                    className={`pi pi-chevron-${
                      expandedSection === "created" ? "up" : "down"
                    } expand-icon`}
                  />
                )}
              </div>
            </SummaryCard>
            <SummaryCard
              variant="success"
              clickable={hasUpdated}
              expanded={expandedSection === "updated"}
              onClick={() => hasUpdated && toggleSection("updated")}
            >
              <div className="count">{importResult.updateCount}</div>
              <div className="label">
                Updated
                {hasUpdated && (
                  <i
                    className={`pi pi-chevron-${
                      expandedSection === "updated" ? "up" : "down"
                    } expand-icon`}
                  />
                )}
              </div>
            </SummaryCard>
            <SummaryCard
              variant="neutral"
              clickable={hasSkipped}
              expanded={expandedSection === "skipped"}
              onClick={() => hasSkipped && toggleSection("skipped")}
            >
              <div className="count">{importResult.skippedPayerCount}</div>
              <div className="label">
                Skipped
                {hasSkipped && (
                  <i
                    className={`pi pi-chevron-${
                      expandedSection === "skipped" ? "up" : "down"
                    } expand-icon`}
                  />
                )}
              </div>
            </SummaryCard>
            <SummaryCard
              variant="warning"
              clickable={hasDuplicates}
              expanded={expandedSection === "duplicates"}
              onClick={() => hasDuplicates && toggleSection("duplicates")}
            >
              <div className="count">{importResult.duplicateCount}</div>
              <div className="label">
                Duplicates
                {hasDuplicates && (
                  <i
                    className={`pi pi-chevron-${
                      expandedSection === "duplicates" ? "up" : "down"
                    } expand-icon`}
                  />
                )}
              </div>
            </SummaryCard>
            <SummaryCard
              variant="error"
              clickable={hasErrors}
              expanded={expandedSection === "errors"}
              onClick={() => hasErrors && toggleSection("errors")}
            >
              <div className="count">{importResult.errorCount}</div>
              <div className="label">
                Errors
                {hasErrors && (
                  <i
                    className={`pi pi-chevron-${
                      expandedSection === "errors" ? "up" : "down"
                    } expand-icon`}
                  />
                )}
              </div>
            </SummaryCard>
          </ResultsSummary>

          {/* Expanded Detail Section */}
          {expandedSection === "created" && hasCreated && (
            <DetailSection>
              <DetailHeader>
                <span className="section-title">
                  Created Orders ({categorizedResults.created.length})
                </span>
              </DetailHeader>
              <DetailList>
                {categorizedResults.created.map((row, index) => (
                  <DetailItem key={index}>
                    <div className="patient-info">
                      <span className="patient-name">{row.patientName}</span>
                      <span className="dos">DOS: {row.dos}</span>
                    </div>
                  </DetailItem>
                ))}
              </DetailList>
            </DetailSection>
          )}

          {expandedSection === "updated" && hasUpdated && (
            <DetailSection>
              <DetailHeader>
                <span className="section-title">
                  Updated Orders ({categorizedResults.updated.length})
                </span>
              </DetailHeader>
              <DetailList>
                {categorizedResults.updated.map((row, index) => (
                  <DetailItem key={index}>
                    <div className="patient-info">
                      <span className="patient-name">{row.patientName}</span>
                      <span className="dos">DOS: {row.dos}</span>
                    </div>
                  </DetailItem>
                ))}
              </DetailList>
            </DetailSection>
          )}

          {expandedSection === "skipped" && hasSkipped && (
            <DetailSection>
              <DetailHeader>
                <span className="section-title">
                  Skipped - Excluded Payer ({categorizedResults.skipped.length})
                </span>
              </DetailHeader>
              <DetailList>
                {categorizedResults.skipped.map((row, index) => (
                  <DetailItem key={index}>
                    <div className="patient-info">
                      <span className="patient-name">{row.patientName}</span>
                      <span className="dos">DOS: {row.dos}</span>
                    </div>
                  </DetailItem>
                ))}
              </DetailList>
            </DetailSection>
          )}

          {expandedSection === "duplicates" && hasDuplicates && (
            <DetailSection>
              <DetailHeader>
                <span className="section-title">
                  Duplicate Orders ({categorizedResults.duplicates.length})
                </span>
              </DetailHeader>
              <DetailList>
                {categorizedResults.duplicates.map((row, index) => (
                  <DetailItem key={index}>
                    <div className="patient-info">
                      <span className="patient-name">{row.patientName}</span>
                      <span className="dos">DOS: {row.dos}</span>
                    </div>
                    <span
                      className={`badge ${
                        row.duplicateType === "hst"
                          ? "badge-hst"
                          : "badge-manual"
                      }`}
                    >
                      {row.duplicateType === "hst"
                        ? "HST Import"
                        : "Manual Order"}
                    </span>
                  </DetailItem>
                ))}
              </DetailList>
            </DetailSection>
          )}

          {expandedSection === "errors" && hasErrors && (
            <DetailSection>
              <DetailHeader>
                <span className="section-title">
                  Errors ({categorizedResults.errors.length})
                </span>
              </DetailHeader>
              <DetailList>
                {categorizedResults.errors.map((row, index) => (
                  <DetailItem key={index}>
                    <div className="patient-info">
                      <span className="patient-name">{row.patientName}</span>
                      <span className="dos">DOS: {row.dos}</span>
                      <span className="error-message">
                        {Object.entries(row.errors || {})
                          .map(([key, value]) => value)
                          .join("; ")}
                      </span>
                    </div>
                  </DetailItem>
                ))}
              </DetailList>
            </DetailSection>
          )}

          {(importResult.successCount > 0 || importResult.updateCount > 0) && (
            <InfoBox>
              <i className="pi pi-info-circle" />
              <span>
                Eligibility checks are running in the background. Orders will
                show updated benefit information once processing is complete.
              </span>
            </InfoBox>
          )}

          {importResult.noInsuranceMatchCount > 0 && (
            <FileInfo>
              Note: {importResult.noInsuranceMatchCount} rows did not have
              matching insurance info.
            </FileInfo>
          )}
        </ResultsContainer>
      );
    }

    return (
      <DialogContent>
        <FileSection>
          <FileLabel>Scheduled Appointments CSV</FileLabel>
          <FileInfo>
            Upload the DE5501 - Scheduled Appointments report from HST
          </FileInfo>
          {scheduledAppointmentsFile ? (
            <SelectedFileDisplay>
              <span>{scheduledAppointmentsFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setScheduledAppointmentsFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleScheduledAppointmentsSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>

        <FileSection>
          <FileLabel>Insurance Verification CSV</FileLabel>
          <FileInfo>
            Upload the OP5017 - Insurance Verification Datasheet from HST
          </FileInfo>
          {insuranceInfoFile ? (
            <SelectedFileDisplay>
              <span>{insuranceInfoFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setInsuranceInfoFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleInsuranceInfoSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>
      </DialogContent>
    );
  };

  return (
    <Dialog
      header="Import Orders from HST"
      visible={visible}
      onHide={handleClose}
      footer={footerContent}
      style={{ width: importResult ? "700px" : "auto" }}
    >
      {renderDialogContent()}
    </Dialog>
  );
}
